import { cloud } from "@vitalsource/vstui/colors";
import styled from "styled-components";
import {
  RoundWhiteButton,
  WhiteButton
} from "../../../components/custom-buttons/white-buttons";
import { Button } from "@vitalsource/vstui";
import React from "react";
import BadgeButton from "../../../components/custom-buttons/badge-button";
import { styledMedia } from "../../../components/mediaquery/breakpoints";

/**
 * Sizing of internals is based on card size, not breakpoints.
 */

const TITLE_SIDE_PADDING_EM = 0.873;
const BUTTON_BOTTOM_PADDING_EM = 0.5;

const isSmall = totalHeight => totalHeight <= 201;

const isLarge = totalHeight => totalHeight >= 280;

const getTitleSizeInfo = totalHeight => {
  if (isSmall(totalHeight)) {
    return {
      height: 41,
      fontSize: 11,
      lineHeight: 13
    };
  } else if (isLarge(totalHeight)) {
    return {
      height: 66,
      fontSize: 16,
      lineHeight: 19
    };
  }
  return {
    height: 44,
    fontSize: 13,
    lineHeight: 15
  };
};
const getButtonSizeInfo = totalHeight => {
  if (isSmall(totalHeight)) {
    return {
      primary: "small",
      secondary: "small",
      badge: "small"
    };
  } else if (isLarge(totalHeight)) {
    return {
      primary: "large",
      secondary: "medium",
      badge: "medium"
    };
  }
  return {
    primary: "medium",
    secondary: "medium",
    badge: "medium"
  };
};
const getButtonTopMarginInfo = (totalHeight, numPrimaryButtons) => {
  if (totalHeight > 305) {
    if (numPrimaryButtons === 1) {
      return "4.5em";
    } else {
      return "3.5em";
    }
  }
  if (numPrimaryButtons === 1) {
    return "3em";
  } else {
    return "2.5em";
  }
};
const getAvailabilityMessageSizeInfo = totalHeight => {
  if (isLarge(totalHeight)) {
    return {
      fontSize: 18,
      marginBottom: 2
    };
  }
  return {
    fontSize: 12,
    marginBottom: 2
  };
};
export const getAvailabilityIconSizeInfo = totalHeight => {
  if (isLarge(totalHeight)) {
    return {
      size: "18px",
      marginBottom: "8px"
    };
  }
  return {
    size: "14px",
    marginBottom: "6px"
  };
};
const getDurationMessageSizeInfo = totalHeight => {
  if (isLarge(totalHeight)) {
    return {
      fontSize: "14px"
    };
  }
  return {
    fontSize: "10px"
  };
};
export const getDurationIconSizeInfo = totalHeight => {
  if (isLarge(totalHeight)) {
    return {
      size: "13px",
      marginRight: "10px"
    };
  }
  return {
    size: "12px",
    marginRight: "5px"
  };
};
export const computeImageHeight = totalHeight => {
  return totalHeight - getTitleSizeInfo(totalHeight).height;
};

export const SkeletonCoverOuter = styled.div`
  ${props => `height: ${props.height}px;`}
  ${props => `width: ${props.width}px;`}
  background: #ffffff;
`;

export const Badge = ({
  cardHeight,
  isButton = true,
  text = "",
  ...restProps
}) => {
  const size = getButtonSizeInfo(cardHeight).badge;
  if (isButton) {
    return <BadgeButton size={size} {...restProps} />;
  } else {
    return (
      <RenderTag cardHeight={cardHeight}>
        <TextTag>{text}</TextTag>
      </RenderTag>
    );
  }
};

export const PrimaryTransparentButton = ({ cardHeight, ...restProps }) => {
  const size = getButtonSizeInfo(cardHeight).primary;
  return <WhiteButton size={size} {...restProps} />;
};

export const PrimaryWhiteButton = ({ cardHeight, ...restProps }) => {
  const size = getButtonSizeInfo(cardHeight).primary;
  return <Button size={size} {...restProps} />;
};

export const RoundButton = ({ cardHeight, favorite, ...restProps }) => {
  const size = getButtonSizeInfo(cardHeight).secondary;
  return <RoundWhiteButton size={size} favorite={favorite} {...restProps} />;
};

export const BookCoverIconOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  width: 100%;
  ${props =>
    typeof props.height === "string" && props.height.slice(-1) === "%"
      ? `height: ${props.height}`
      : `height: ${props.height}px;`}
  ${props => `width: ${props.width}px;`}
`;

// TODO:
// - this shouldn't react to hover/focus
export const BookCoverOuter = styled.div`
  display: flex;
  flex-direction: column-reverse;
  position: relative;
  cursor: pointer;
  ${props => (props.underline ? `border-bottom: 3px solid #0075a7;` : null)}
  .library-book-cover-outer {
    overflow: hidden;
    &:after {
      pointer-events: none;
      display: block;
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      ${props =>
        props.hero
          ? `height: 100%`
          : `height: calc(100% - ${getTitleSizeInfo(props.height).height}px)`};
      ${props => `font-size: ${getTitleSizeInfo(props.height).fontSize}px)`};
      opacity: 0;
      transition: opacity 200ms;
      background: linear-gradient(
        -180deg,
        rgba(0, 0, 0, 0.8) 0%,
        rgba(0, 0, 0, 0.4) 100%
      );
      /* Fix corner flashing during transition in Safari */
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
    }
  }
  ${props => `height: ${props.height}px;`}
  ${props => `width: ${props.width}px;`}
  &:hover,
  &:focus,
  &.hasFocus {
    .library-book-cover-outer {
      overflow: hidden;
      &:after {
        opacity: 1;
      }
    }

    .library-book-cover-inner {
      filter: blur(5px);
    }
  }
`;

export const Title = styled.h3`
  color: ${cloud.getShade(10)};
  font-weight: 500;
  padding: 0 ${TITLE_SIDE_PADDING_EM}em;
  border-top: 1px solid ${cloud.getShade(3)};
  ${props => {
    const info = getTitleSizeInfo(props.cardHeight);
    // font size is set at an outer layer so other pieces can use em units
    return `
      height: ${info.height}px;
      line-height: ${info.lineHeight}px;
      padding-top: ${(info.height - info.lineHeight * 2) / 2}px;
    `;
  }}
  span {
    font-weight: 300;
  }
`;

export const PrimaryButtons = styled.div`
  order: 2;
  opacity: 0;
  margin-right: 1000px;
  margin-left: -1000px;
  width: 100%;
  left: 0;
  ${props => {
    return `margin-top: ${getButtonTopMarginInfo(
      props.cardHeight,
      props.numPrimaryButtons
    )}`;
  }}
  ${props => `
    padding: ${TITLE_SIDE_PADDING_EM}em ${TITLE_SIDE_PADDING_EM}em ${
    getTitleSizeInfo(props.cardHeight).height
  }px;
  `}

  & > * {
    margin-bottom: ${BUTTON_BOTTOM_PADDING_EM}em;
  }
`;

export const RenderTag = styled.div`
  margin: 0;
  background-color: ${props => props.theme.vstui.color.secondary.getShade(6)};
  border-radius: 16px;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.5);
  height: 30px;
  line-height: 2.14;
  padding: 0 11px;
  position: absolute;
  right: 0;
  top: -15px;
  vertical-align: middle;
  white-space: nowrap;
  ${styledMedia.medium`
    height: 28px;
    line-height: 2;
    padding: 0 9px;
  `};
  ${styledMedia.small`
    height: 26px;
    line-height: 1.86;
    padding: 0 9px;
    right: -5px;
    top: -16px;
  `};
  ${styledMedia.tiny`
    height: 20px;
    line-height: 1.4;
    right: -5px;
    padding: 0 5px;
    top: -58px;
  `};
`;

export const TextTag = styled.p`
  color: ${props => props.theme.vstui.color.white};
`;

export const SecondaryButtons = styled.div`
  order: 1;
  opacity: 0;
  margin-right: 1000px;
  margin-left: -1000px;
  min-height: 32px;
  & > * {
    margin-left: ${TITLE_SIDE_PADDING_EM * 0.8}em;
  }
`;

export const Badges = styled.div`
  order: 4;
  position: relative;
  margin: 0;
  button {
    padding: 0 16px;
  }
  ${styledMedia.medium`
    margin:  0;
  `};
  ${styledMedia.small`
    margin:  0;
  `};
  ${styledMedia.tiny`
    margin: 0;
  `};
`;

export const AvailabilityMessage = styled.div`
  order: 2;
  opacity: 0;
  margin-right: 1000px;
  margin-left: -1000px;
  color: #ffffff;
  font-size: 1.8rem;
  font-style: italic;
  text-align: center;
  position: absolute;
  top: 58px;
  width: 100%;
  left: 0;
  ${props => {
    const info = getAvailabilityMessageSizeInfo(props.cardHeight);
    return `
      font-size: ${info.fontSize}px;
    `;
  }}

  svg {
    display: inline-block;
    ${props => {
      const info = getAvailabilityIconSizeInfo(props.cardHeight);
      return `
        width: ${info.size};
        height: ${info.size};
        margin-bottom: ${info.marginBottom};
      `;
    }}
  }
`;

export const DurationMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  order: 3;
  opacity: 0;
  margin-right: 1000px;
  margin-left: -1000px;
  color: #ffffff;
  width: 100%;
  font-style: italic;
  white-space: nowrap;
  ${props => {
    const info = getDurationMessageSizeInfo(props.cardHeight);
    return `
      font-size: ${info.fontSize};
    `;
  }}
  svg {
    ${props => {
      const info = getDurationIconSizeInfo(props.cardHeight);
      return `
        margin-right: ${info.marginRight};
        width: ${info.size};
        height: ${info.size};
      `;
    }}
  }
`;

export const ButtonWrapper = styled.div`
  height: 100%;
  position: absolute;
  top: 0px;
  width: 100%;
  text-align: right;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${props => `
    padding: ${TITLE_SIDE_PADDING_EM}em ${TITLE_SIDE_PADDING_EM}em ${
    getTitleSizeInfo(props.cardHeight).height
  }px;
  `}

  &:active,
  &:focus,
  &:hover,
  &.hasFocus {
    ${/* sc-selector */ PrimaryButtons}, ${
  /* sc-selector */ SecondaryButtons
}, ${/* sc-selector */ DurationMessage}, ${
  /* sc-selector */ AvailabilityMessage
} {
      opacity: 1;
      margin-right: 0px;
      margin-left: 0px;
    }
  }
`;
